/**
 * AI CONTEXT
 * Purpose: app-level Firebase/Jotai data + attendance mutation helpers.
 * Provides: allStudents/allUsers, tag/allergy maps, student/teacher attendance update APIs.
 * Used by: attendance flows in admin/teacher views and shared components.
 * Importance: mutation boundary for common operational writes.
 * Refactor: extract attendance mutation logic into service hook with stricter types.
 */
import { noop } from "lodash"
import { createContext, type ReactNode, useContext, useMemo } from "react"

import { allergyAtoms, studentAtoms, tagAtoms, userAtoms } from "#atoms"
import Firebase from "#classes/Firebase"
import Log from "#classes/Log"
import { type AllergyConstructor } from "#customTypes/composedTypes/allergyTypes"
import { DOC_TYPES } from "#customTypes/composedTypes/firebaseTypes"
import { StudentConstructor } from "#customTypes/composedTypes/studentTypes"
import { type TagConstructor } from "#customTypes/composedTypes/tagTypes"
import { UserConstructor } from "#customTypes/composedTypes/userTypes"
import { Uuid } from "#customTypes/globalTypes"
import { AttendanceStatus, invariant } from "#customTypes/types"
import { useAllDocs } from "#hooks/jotai/useSubscription"
import useAnalytics from "#hooks/useAnalytics"
import { useErrorModal } from "#hooks/useErrorModal"
import { generateSessionId } from "#utils/classHelpers"
import { ErrorType } from "#utils/textFormatters"

export interface FirebaseContextType {
  isReady: boolean

  // Sessions

  // Students
  allStudents: StudentConstructor[] | undefined
  onStudentAttendanceChange: (
    student: StudentConstructor,
    classId: Uuid,
    sessionIndex: number,
    status: AttendanceStatus,
    attendanceField?: "attendance" | "assistantAttendance",
    projectId?: Uuid
  ) => void

  // Exceptions
  // onAssistantRoomChange
  // onAddNewException

  // Events
  // events,

  // Employees
  allUsers: UserConstructor[] | undefined
  onTeacherAttendanceChange: (
    user: UserConstructor,
    classId: Uuid,
    sessionIndex: number,
    status: AttendanceStatus
  ) => void

  // Tag & Allergy options (subscribed once here, consumed everywhere)
  // Keyed by value string -> { label, icon }
  tagsByValue: Record<string, { label: string; icon: string }>
  allergiesByValue: Record<string, { label: string; icon: string }>
}

export const FirebaseContext = createContext<FirebaseContextType>({
  isReady: false,

  // Students
  allStudents: [],
  onStudentAttendanceChange: noop,

  // Employees
  allUsers: [],
  onTeacherAttendanceChange: noop,

  // Tag & Allergy options
  tagsByValue: {},
  allergiesByValue: {},
})

const FirebaseContextProvider = ({ children }: { children: ReactNode }) => {
  // Use Jotai subscriptions - automatically managed and pooled
  const allStudents = useAllDocs<StudentConstructor>(
    studentAtoms,
    DOC_TYPES.STUDENTS
  )

  const allUsers = useAllDocs<UserConstructor>(userAtoms, DOC_TYPES.USERS)

  const allTags = useAllDocs<TagConstructor>(tagAtoms, DOC_TYPES.TAGS)
  const allAllergies = useAllDocs<AllergyConstructor>(
    allergyAtoms,
    DOC_TYPES.ALLERGIES
  )

  const tagsByValue = useMemo(() => {
    const map: Record<string, { label: string; icon: string }> = {}
    for (const tag of allTags) {
      map[tag.id] = { label: tag.label, icon: tag.icon }
    }
    return map
  }, [allTags])

  const allergiesByValue = useMemo(() => {
    const map: Record<string, { label: string; icon: string }> = {}
    for (const allergy of allAllergies) {
      map[allergy.id] = { label: allergy.label, icon: allergy.icon }
    }
    return map
  }, [allAllergies])

  const firebase = Firebase.getInstance()

  const { trackAttendanceAction } = useAnalytics()
  const handleAttendanceStatusChange = async (
    student: StudentConstructor,
    classId: Uuid,
    sessionIndex: number,
    newStatus: AttendanceStatus,
    attendanceField: "attendance" | "assistantAttendance" = "attendance",
    projectId?: Uuid
  ) => {
    try {
      invariant(
        attendanceField === "attendance" ||
          attendanceField === "assistantAttendance",
        "Invalid attendance field"
      )

      invariant(
        !!student[attendanceField]?.[classId],
        `Class ${classId} attendance not found for ${Log.student(student)}`
      )

      await firebase.setAttendanceStatus({
        docType: DOC_TYPES.STUDENTS,
        id: student.id,
        attendanceField,
        classId,
        sessionIndex,
        status: newStatus,
        projectId,
      })

      const sessionId = generateSessionId(classId, sessionIndex)
      trackAttendanceAction({
        studentId: student.id,
        classId,
        sessionId,
        status: newStatus,
      })
    } catch (error) {
      const context = `
FirebaseContextProvider
`
      openErrorModal(error as ErrorType, context)
    }
  }

  const handleTeacherAttendanceChange = async (
    user: UserConstructor,
    classId: Uuid,
    sessionIndex: number,
    newStatus: AttendanceStatus
  ) => {
    try {
      invariant(
        !!user.teacherAttendance,
        `Class ${classId} teacherAttendance not found for user ${user.name} (${user.id})`
      )

      await firebase.setAttendanceStatus({
        docType: DOC_TYPES.USERS,
        id: user.id,
        attendanceField: "teacherAttendance",
        classId,
        sessionIndex,
        status: newStatus,
      })
    } catch (error) {
      const context = `
FirebaseContextProvider - Teacher Attendance
`
      openErrorModal(error as ErrorType, context)
    }
  }

  const openErrorModal = useErrorModal("viewing FirebaseContextProvider")

  const value = {
    isReady: !!firebase,
    // Sessions

    // Students
    allStudents,
    onStudentAttendanceChange: handleAttendanceStatusChange,

    // Exceptions
    // onAssistantRoomChange
    // onAddNewException

    // Events
    // events,

    // Employees
    allUsers,
    onTeacherAttendanceChange: handleTeacherAttendanceChange,

    // Tag & Allergy options
    tagsByValue,
    allergiesByValue,
  }

  return (
    <FirebaseContext.Provider value={value}>
      {children}
    </FirebaseContext.Provider>
  )
}

export const useFirebaseContext = () => useContext(FirebaseContext)

export default FirebaseContextProvider
